import { QuestionStep } from '../QuestionStep';
import { ChoiceCard } from './_shared';
import {
  BORDER_SHAPE_OPTIONS,
  BORDER_LINE_OPTIONS,
  STEPS,
  type QuestionnaireAnswers,
} from '../questions';

interface BorderStepProps {
  answers: QuestionnaireAnswers;
  onChange: (next: Partial<QuestionnaireAnswers>) => void;
}

const SHAPE_PREVIEW: Record<string, { radius: number; clip?: string }> = {
  squared: { radius: 0 },
  rounded: { radius: 12 },
  beveled: { radius: 0, clip: 'polygon(8% 0, 100% 0, 100% 92%, 92% 100%, 0 100%, 0 8%)' },
};

export function BorderStep({ answers, onChange }: BorderStepProps) {
  const step = STEPS.find((s) => s.id === 'border')!;

  return (
    <QuestionStep step={step}>
      <div className="grid gap-10">
        <div>
          <p className="smallcaps mb-3 text-graphite">Corner shape</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {BORDER_SHAPE_OPTIONS.map((opt) => {
              const preview = SHAPE_PREVIEW[opt.value];
              return (
                <ChoiceCard
                  key={opt.value}
                  selected={answers.border_shape === opt.value}
                  onSelect={() => onChange({ border_shape: opt.value })}
                  label={opt.label}
                  hint={opt.hint}
                  preview={
                    <div
                      aria-hidden
                      className="h-12 w-full bg-ink/10"
                      style={{
                        borderRadius: preview?.radius ?? 0,
                        clipPath: preview?.clip,
                      }}
                    />
                  }
                />
              );
            })}
          </div>
        </div>

        <div>
          <p className="smallcaps mb-3 text-graphite">Line style</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BORDER_LINE_OPTIONS.map((opt) => (
              <ChoiceCard
                key={opt.value}
                selected={answers.border_line === opt.value}
                onSelect={() => onChange({ border_line: opt.value })}
                label={opt.label}
                hint={opt.hint}
                preview={
                  <div
                    aria-hidden
                    className="h-12 w-full"
                    style={
                      opt.value === 'none'
                        ? {
                            // Borderless preview: faint dashed background
                            // outline so the swatch reads as "no border"
                            // rather than an empty rectangle.
                            background:
                              'repeating-linear-gradient(45deg, hsl(var(--ink) / 0.04), hsl(var(--ink) / 0.04) 4px, transparent 4px, transparent 8px)',
                            borderRadius: 4,
                          }
                        : {
                            border: `2px ${opt.value} hsl(var(--ink))`,
                            borderRadius: 4,
                          }
                    }
                  />
                }
              />
            ))}
          </div>
        </div>
      </div>
    </QuestionStep>
  );
}
